import React from 'react';
import { ExternalLink } from 'lucide-react';

export interface Amendment {
  amendment_number: string;
  url: string;
  date: string;
  status: string;
}

/**
 * Shared amendment cell content used in BillTable (bill tracking report) and ReportGrid (custom reports).
 * Renders the same button/link and "No amendments" span in both places.
 */
export interface AmendmentCellProps {
  /** Display text e.g. "Feb 02, 2026 (1)" */
  displayText: string;
  /** Whether this row has amendments (show as link/button vs plain "No amendments") */
  hasAmendments: boolean;
  /** When provided, render as a real link (ReportGrid). Takes precedence over onClick. */
  href?: string;
  /** When provided (and no href), render as button and call on click (BillTable). */
  onClick?: (e: React.MouseEvent) => void;
  /** When true and hasAmendments: render span that looks like a link only; click is handled by parent (e.g. AG Grid onCellClicked). Use when the grid captures cell clicks. */
  linkAppearanceOnly?: boolean;
  /** When true, use native <a> navigation (no preventDefault). Use in AG Grid so the link works even if the grid captures React events. */
  useNativeNavigation?: boolean;
  /** Individual amendments — when provided, each renders as a direct external link. */
  amendments?: Amendment[];
}

const amendmentLinkClass =
  'text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline cursor-pointer text-left';

function formatAmendmentDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export function AmendmentCell({ displayText, hasAmendments, href, onClick, linkAppearanceOnly, useNativeNavigation, amendments }: AmendmentCellProps) {
  if (amendments && amendments.length > 0) {
    return (
      <div className="space-y-1 min-w-0" onClick={(e) => e.stopPropagation()}>
        {amendments.map((a, i) => (
          <div key={i} className="flex items-center gap-1">
            {a.url ? (
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${amendmentLinkClass} truncate`}
                title={`Amendment ${a.amendment_number}${a.status ? ` – ${a.status}` : ''}`}
              >
                {a.amendment_number}{a.date ? ` (${formatAmendmentDate(a.date)})` : ''}
                <ExternalLink className="w-3 h-3 inline ml-1 flex-shrink-0" />
              </a>
            ) : (
              <span className="text-sm text-text-primary dark:text-text-dark-primary truncate">
                {a.amendment_number}{a.date ? ` (${formatAmendmentDate(a.date)})` : ''}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (hasAmendments && linkAppearanceOnly) {
    return (
      <span className={`${amendmentLinkClass} block w-full min-w-0`} data-amendment-link="true">
        {displayText}
      </span>
    );
  }
  if (hasAmendments && (href || onClick)) {
    const effectiveHref = href ?? '#';
    const isNative = useNativeNavigation && effectiveHref !== '#';
    return (
      <a
        href={effectiveHref}
        className={`${amendmentLinkClass} block w-full min-w-0`}
        data-amendment-link="true"
        onClick={(e) => {
          e.stopPropagation();
          if (!isNative) {
            e.preventDefault();
            if (onClick) onClick(e);
            else if (href) window.open(href, '_blank', 'noopener,noreferrer');
          }
        }}
        target={isNative ? '_self' : (onClick ? undefined : '_blank')}
        rel={isNative ? 'noopener noreferrer' : (onClick ? undefined : 'noopener noreferrer')}
      >
        {displayText}
      </a>
    );
  }
  return (
    <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
      {displayText || 'No amendments'}
    </span>
  );
}
